import {Box} from "@mantine/core";
import cx from "clsx";
import commonClasses from "../common.module.css";
import {
    BlockTypeSelect,
    BoldItalicUnderlineToggles, DiffSourceToggleWrapper,
    headingsPlugin,
    listsPlugin, ListsToggle,
    MDXEditor, MDXEditorMethods,
    toolbarPlugin,
    UndoRedo,
    diffSourcePlugin
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import React, {ReactNode, useEffect, useRef} from "react";
import classes from "./MarkdownEditor.module.css";

import {BoxProps} from "@mantine/core";

export type TMarkdownEditingMode = "full" | "checkboxes" | "none";

interface MarkdownEditorProps extends BoxProps {
    markdown: string
    onChange?: (markdown: string) => void
    title?: ReactNode
    mdDataTestId?: string
    editingMode?: TMarkdownEditingMode
}

/**
 * A Markdown editor component that supports viewing and editing markdown content.
 */
export const MarkdownEditor = React.forwardRef<HTMLDivElement, MarkdownEditorProps>(({
                                                                                         title,
                                                                                         markdown,
                                                                                         onChange = () => "",
                                                                                         editingMode = "none",
                                                                                         mdDataTestId,
                                                                                         ...others
                                                                                     }, ref) => {
    const markdownRef = useRef<MDXEditorMethods>(null);
    const fullEditing = editingMode === "full";
    const checkboxEditing = editingMode === "checkboxes";

    useEffect(() => {
        if (markdownRef.current) {
            markdownRef.current.setMarkdown(markdown);
        }
    }, [markdown]);

    const plugins = fullEditing ? [headingsPlugin(), listsPlugin(), diffSourcePlugin({
        viewMode: 'rich-text',
    }),
        toolbarPlugin({
            toolbarClassName: "markdown-toolbar",
            toolbarContents: () => fullEditing ? (
                <DiffSourceToggleWrapper options={['rich-text', 'source']}>
                    <UndoRedo/>
                    <BoldItalicUnderlineToggles/>
                    <ListsToggle/>
                    <BlockTypeSelect/>
                </DiffSourceToggleWrapper>
            ) : <></>

        })] : [headingsPlugin(), listsPlugin()]
    const onMarkdownChanged = (newMarkdown: string) => {
        // "full" mode accepts all edits.
        if (fullEditing) {
            onChange?.(newMarkdown);
            return;
        }
        // "checkboxes" mode accepts only ±1-char checkbox toggles.
        if (checkboxEditing &&
            Math.abs(newMarkdown.length - markdown.length) <= 1 &&  // same or ±1 char
            newMarkdown.replace(/[-*\s[x\]]/g, '') === markdown.replace(/[-*\s[x\]]/g, '') // ignore list & checkbox chars
        ) {
            onChange?.(newMarkdown); // accept toggle
            return;
        }
        // "none" mode (and any disallowed change in checkboxes mode) reverts.
        markdownRef.current?.setMarkdown(markdown);
    }

    return (<Box ref={ref} className={cx(fullEditing && commonClasses.border, commonClasses.sbRadius)}
                 aria-readonly={!fullEditing}
                 style={{display: "grid", grid: "min-content auto / 1fr"}} {...others}>
            {title ? <Box className={cx(commonClasses.textCenter)}>{title}</Box> : <></>}
            <Box mah="15em" className={cx(commonClasses.markdown, commonClasses.tgtBorder, classes.matchingBrackets)}
                 style={{overflow: "auto", boxShadow: fullEditing ? "" : "none", border: fullEditing ? "" : "none"}}>
                <MDXEditor ref={markdownRef} markdown={markdown}
                           // don't use this in edit mode as every type will cause it to lose focus
                           onChange={(value) => onMarkdownChanged(value)}
                           data-testid={mdDataTestId || "markdown-editor"}
                           readOnly={editingMode === "none"}
                           plugins={plugins}
                           // only accept changes when we lose focus
                           onBlur={() => onMarkdownChanged(markdownRef.current?.getMarkdown() ?? '')}
                />
            </Box>
        </Box>
    )
})